import type { AxiosInstance } from 'axios'
import ReactDOM from 'react-dom/client'

import '@suntex.classic/createjs'
import { Client } from '@suntex.fim/api-client'
import { FrameworkProvider } from '@suntex.fim/framework'
import { registerWindowGlobals } from '@suntex.fim/player'

import App from './App'
import ErrorBoundary from './pages/ErrorBoundary'
import { getLocale } from './utils/getLocale'
import { initGoogleAnalytics } from './utils/googleAnalytics'
import { appShowError } from './utils/utilities'

const baseURL = import.meta.env.VITE_API_URL as string

const apiVersion = import.meta.env.VITE_API_VERSION as string | undefined
if (apiVersion) {
  Client.DEFAULT_API_VERSION = apiVersion
}

function isJsonContentType(headers: Record<string, string> | undefined) {
  const ct = headers && (headers['content-type'] || headers['Content-Type'])
  return (
    !!ct &&
    typeof ct === 'string' &&
    ct.toLowerCase().includes('application/json')
  )
}

function setupInterceptors(axiosInstance: AxiosInstance) {
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      // 401s are handled by the framework interceptor (session expiry modal)
      const status =
        error != null &&
        typeof error === 'object' &&
        'response' in error &&
        error.response != null &&
        typeof error.response === 'object' &&
        'status' in error.response
          ? (error.response as { status: unknown }).status
          : undefined

      if (status !== 401) {
        appShowError(error.message, error)
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const response = error && error.response
      if (
        response &&
        response.data instanceof Blob &&
        isJsonContentType(response.headers)
      ) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const text = await response.data.text()
          // Replace the blob with plain text JSON so the generated client can parse it
          response.data = text as Error
        } catch (_e) {
          // if conversion fails, leave it as-is
        }
      }
      return Promise.reject(error)
    }
  )
}

// Initialize window globals synchronously at module scope so embedded module
// code that calls them during initialization (e.g. via short setTimeout) can
// always find them defined, regardless of React's render timing.
registerWindowGlobals({
  backToMenu: () => {
    window.location.href = '/player'
  }
})

initGoogleAnalytics()

/**
 * wrapping the application in providers and rendering it in Id named 'root' in DOM
 */
ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <ErrorBoundary>
    <FrameworkProvider
      apiBaseURL={baseURL}
      locale={getLocale()}
      setupInterceptors={setupInterceptors}
      onSoundToggle={(disabled) => {
        // Disables sound in the classic games.  Ported games are already handled the framework.
        if (disabled) LoadUtil.mute()
        else LoadUtil.unmute()
      }}
    >
      <App />
    </FrameworkProvider>
  </ErrorBoundary>
)
